import { LitElement, html, css } from 'lit';

/**
 * Component events.
 */
const CUSTOM_EVENTS = {
  cancel: 'cancel',
  error: 'error',
  ok: 'success',
  goTo: 'goTo',
};

const MODE_ENUM = Object.freeze({
  sync: 'sync',
  async: 'async',
});

const ERROR_MESSAGES = {
  key: 'key parameter can´t be empty',
  mailingLists: `mailingLists parameter can´t be empty in ${MODE_ENUM.sync} mode`,
  environment:
    'environment parameter does not correspond to the possible values',
  mode: `mode parameter should be ${MODE_ENUM.sync} or ${MODE_ENUM.async}`,
};

const ENVIRONMENTS = ['dev', 'pre', 'beta', 'pro'];

export class UnsubscribeCommunications extends LitElement {
  static get styles() {
    return [
      css`
        :host {
          display: block;
        }

        :host .disabled {
          display: none;
        }

        .unsubscribe-communications--container {
          position: relative;
        }

        .unsubscribe-communications--img-container {
          width: 100%;
          height: 142px;
          background-position: var(
            --unsubscribe-communications-img-position,
            40% 40%
          );
          background-size: cover;
        }

        .unsubscribe-communications--content {
          background-color: #fff;
          width: calc(100% - 16px);
          left: 8px;
          top: 0;
          position: relative;
          margin-top: -72px;
          padding: 32px 24px;
          box-sizing: border-box;
        }

        .unsubscribe-communications--buttons {
          width: 100%;
          display: flex;
          flex-direction: column;
          margin-top: 56px;
        }

        .unsubscribe-communications--button {
          width: 100%;
          padding: 12px 24px;
          font-family: var(--unsubscribe-communications-font-family);
          font-size: var(--unsubscribe-communications-button-font-size, 15px);
          line-height: 1.33;
          letter-spacing: 0.3px;
          border-radius: var(
            --unsubscribe-communications-button-border-radius,
            4px
          );
          outline: 0;
          cursor: pointer;
          box-sizing: border-box;
          text-decoration: none;
        }

        .unsubscribe-communications--spinner {
          display: block;
          margin-left: calc((100% / 2) - 10px);
          border: 3px solid #fff;
          border-top: 3px solid #ff4242;
          border-right: 3px solid #ff4242;
          border-bottom: 3px solid #ff4242;
          border-radius: 50%;
          width: 13px;
          height: 13px;
          animation: spin 1s linear infinite;
        }

        .unsubscribe-communications--button-secondary {
          order: 2;
          color: var(--unsubscribe-communications-secondary-button-color, #fff);
          border: var(
            --unsubscribe-communications-secondary-button-border,
            1px solid #0e191c
          );
          background-color: var(
            --unsubscribe-communications-secondary-button-bk-color,
            #0e191c
          );
        }

        .unsubscribe-communications--button-secondary:hover {
          color: var(
            --unsubscribe-communications-secondary-button-color-hover,
            #fff
          );
          border: var(
            --unsubscribe-communications-secondary-button-border-hover,
            1px solid #0e191c
          );
          background-color: var(
            --unsubscribe-communications-secondary-button-bk-color-hover,
            #0e191c
          );
        }

        .unsubscribe-communications--button-primary {
          order: 1;
          color: var(--unsubscribe-communications-primary-button-color, #fff);
          border: var(
            --unsubscribe-communications-primary-button-border,
            1px solid #ff4242
          );
          background-color: var(
            --unsubscribe-communications-primary-button-bk-color,
            #ff4242
          );
        }

        .unsubscribe-communications--button-primary:hover {
          color: var(
            --unsubscribe-communications-primary-button-color-hover,
            #fff
          );
          border: var(
            --unsubscribe-communications-primary-button-border-hover,
            1px solid #ff4242
          );
          background-color: var(
            --unsubscribe-communications-primary-button-bk-color-hover,
            #ff4242
          );
        }

        .unsubscribe-communications--button-secondary
          ~ .unsubscribe-communications--button-primary {
          margin-bottom: 16px;
        }

        .unsubscribe-communications--container-secondary {
          text-align: center;
          margin: 0 auto;
          padding: 0 8px;
          width: 100%;
          max-width: 600px;
          box-sizing: border-box;
        }

        .unsubscribe-communications--image-secondary {
          width: 170px;
          height: auto;
          margin-bottom: 32px;
        }

        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        @media (min-width: 768px) {
          .unsubscribe-communications--img-container {
            width: 100%;
            max-width: 545px;
            height: 535px;
          }

          .unsubscribe-communications--content {
            width: auto;
            padding: 56px 64px;
            position: absolute;
            left: 30%;
            top: 80px;
            right: 0px;
            margin-top: 0;
          }

          .unsubscribe-communications--buttons {
            flex-direction: row;
            justify-content: center;
          }

          .unsubscribe-communications--button {
            min-width: 200px;
            width: auto;
          }

          .unsubscribe-communications--button-secondary {
            order: 0;
            margin-right: 16px;
          }

          .unsubscribe-communications--button-primary {
            order: 0;
          }

          .unsubscribe-communications--button-secondary
            ~ .unsubscribe-communications--button-primary {
            margin-left: 16px;
            margin-bottom: 0;
          }
        }

        @media (min-width: 1024px) {
          .unsubscribe-communications--content {
            left: calc(545px - 64px);
          }
        }
      `,
    ];
  }

  static get properties() {
    return {
      images: { type: Object },
      buttonsText: { type: Object },
      mailingLists: { type: Array },
      key: { type: String },
      environment: { type: String },
      mode: { type: String },
      language: { type: String },
    };
  }

  render() {
    return html`
      ${!this.unsubscribe
        ? html`
            <section
              class="unsubscribe-communications--container ${this.isDisabled
                ? 'disabled'
                : ''}"
            >
              ${this.images && this.images.firstPage
                ? html`
                    <div
                      class="unsubscribe-communications--img-container"
                      style="background-image: url('${this.images.firstPage}')"
                    ></div>
                  `
                : html``}
              <div class="unsubscribe-communications--content">
                <slot name="firstPage"></slot>
                <div class="unsubscribe-communications--buttons">
                  <button
                    class="unsubscribe-communications--button unsubscribe-communications--button-secondary"
                    @click="${this.cancelUnsubscribe}"
                  >
                    ${this.buttonsText.cancel}
                  </button>
                  <button
                    class="unsubscribe-communications--button unsubscribe-communications--button-primary"
                    @click="${this.unsubscribeCommunications}"
                  >
                    ${this.isLoading
                      ? html`
                          <span
                            class="unsubscribe-communications--spinner"
                          ></span>
                        `
                      : html` <span>${this.buttonsText.unsubscribe}</span> `}
                  </button>
                </div>
              </div>
            </section>
          `
        : html`
            <section
              class="unsubscribe-communications--container-secondary"
              aria-live="polite"
            >
              ${this.images && this.images.secondPage
                ? html`
                    <img
                      class="unsubscribe-communications--image-secondary"
                      src="${this.images.secondPage}"
                      alt="${this.buttonText?.unsubscribe} ok"
                    />
                  `
                : html``}
              <slot name="secondPage"></slot>
              <div class="unsubscribe-communications--buttons">
                <button
                  class="unsubscribe-communications--button unsubscribe-communications--button-primary"
                  @click="${this.goTo}"
                >
                  ${this.buttonsText.goTo}
                </button>
              </div>
            </section>
          `}
    `;
  }

  constructor() {
    super();
    this.buttonsText = {};
    this.unsubscribe = false;
    this.isLoading = false;
    this.isDisabled = true;
    this.environment = '';
    this.mode = MODE_ENUM.sync;
    this.language = 'es-ES';
  }

  /**
   * Check that the mandatory properties for the cancellation of commercial communications have value.
   */
  updated(changedProperties) {
    if (changedProperties.size !== 0) {
      try {
        if (this.environment && !ENVIRONMENTS.includes(this.environment)) {
          throw new Error(ERROR_MESSAGES.environment);
        }

        if (!this.key) {
          throw new Error(ERROR_MESSAGES.key);
        }

        if (
          this.mode === MODE_ENUM.sync &&
          (!this.mailingLists || this.mailingLists.length < 1)
        ) {
          throw new Error(ERROR_MESSAGES.mailingLists);
        }

        if (!this._validateMode(this.mode)) {
          throw new Error(ERROR_MESSAGES.mode);
        }

        this.isDisabled = false;
        this.requestUpdate();
      } catch (err) {
        this.isDisabled = true;
        console.error(err);
        this.requestUpdate();
      }
    }
  }

  /**
   * Send an event indicating that the user wants to cancel the operation.
   */
  cancelUnsubscribe() {
    const event = new Event(CUSTOM_EVENTS.cancel);
    this.dispatchEvent(event);
  }

  /**
   * Sends an event indicating that the user wants to exit the screen.
   */
  goTo() {
    const event = new Event(CUSTOM_EVENTS.goTo);
    this.dispatchEvent(event);
  }

  /**
   * Start the process of unsubscribing commercial communications.
   * If everything goes well, send a success event. If there are any errors, submit an error event.
   */
  unsubscribeCommunications() {
    if (!this.isLoading) {
      this.isLoading = true;
      this.requestUpdate();

      const data = this._dataToSend();
      const url = this._getUnsubscribeUrl();
      const options = {
        method: 'POST',
        body: JSON.stringify(data),
        headers: new Headers({
          'Content-Type': 'application/json',
        }),
      };

      fetch(url, options)
        .then(async res => {
          if (!res.ok) {
            const error = res instanceof Response ? await res.json() : res;
            throw error;
          }

          this.unsubscribe = true;
          this._sendCustomEvent(CUSTOM_EVENTS.ok);
          await this.requestUpdate();
          this._focusOnGoTo();
        })
        .catch(error => {
          this._sendCustomEvent(CUSTOM_EVENTS.error, error);
          this.isLoading = false;
          this.requestUpdate();
        });
    }
  }

  _dataToSend() {
    return {
      key: this.key,
      listId: this.mailingLists,
      mode: this.mode,
      language: this.language,
    };
  }

  _getUnsubscribeUrl() {
    const env =
      this.environment && this.environment !== 'pro'
        ? `.${this.environment}`
        : '';
    return `https://api-manager${env}.universia.net/coreplatform-communication-mgmt/api/unsubscribe`;
  }

  _sendCustomEvent(type, data) {
    const event = new CustomEvent(type, {
      detail: data,
    });
    this.dispatchEvent(event);
  }

  _validateMode(mode) {
    return MODE_ENUM[`${mode}`];
  }

  _focusOnGoTo() {
    const goToButton = this.shadowRoot.querySelector(
      '.unsubscribe-communications--button.unsubscribe-communications--button-primary'
    );
    goToButton.focus();
  }
}
